import { useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { resetSnackbar } from "store/commonSlice";
import { httpStatusOf } from "utils/httpStatusOf";

export const TEN_MINUTES_MS = 10 * 60 * 1000;
export const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
export const TWENTY_MINUTES_MS = 20 * 60 * 1000;
export const THIRTY_MINUTES_MS = 30 * 60 * 1000;
export const FORTY_FIVE_MINUTES_MS = 45 * 60 * 1000;
export const SIXTY_MINUTES_MS = 60 * 60 * 1000;

/**
 * Custom hook to handle recursive API calls with a timeout
 * @param maxDuration Maximum duration in milliseconds before canceling recursive calls
 * @returns Methods and state for managing the recursive API calls
 */
const useTimeoutApiCall = (maxDuration = TEN_MINUTES_MS) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const isActiveRef = useRef<boolean>(false);
  const minuteLoggerRef = useRef<NodeJS.Timeout | null>(null);
  const lastLoggedMinuteRef = useRef<number>(0);
  /** Which recursive RUN is current. Bumped by every start and every clear.
   *
   * `[SELECTION_ANCHOR_BY_IDENTITY]` — an obsolete continuation must not touch the live scheduler.
   * `executeWithTimeout` awaits `recurFn()` and then writes `timeoutRef`, but by the time a slow
   * call resolves that ref can already belong to a DIFFERENT run: the continuation of run A either
   * cleared run B's pending timer (when A returned "stop") or overwrote it with A's own (when A
   * returned "continue"), so B silently lost its polling and its timeout — the user sits on a
   * spinner that will never resolve. Surfaced by `useSocketFallback`'s stale-run guard, which makes
   * an obsolete poll return "stop" rather than settling the wrong job.
   */
  const runRef = useRef(0);

  // Clear any active timeout
  const clearRecursiveTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Clear minute logger
    if (minuteLoggerRef.current) {
      clearInterval(minuteLoggerRef.current);
      minuteLoggerRef.current = null;
    }

    // Log elapsed time if we had a start time (only in development)
    if (startTimeRef.current !== null && process.env.NODE_ENV === "development") {
      const elapsedTime = Date.now() - startTimeRef.current;
      const minutes = Math.floor(elapsedTime / 60000);
      const seconds = ((elapsedTime % 60000) / 1000).toFixed(1);
      console.log(`Clearing timeout after ${elapsedTime}ms (${minutes}m ${seconds}s)`);
    }

    startTimeRef.current = null;
    isActiveRef.current = false;
    lastLoggedMinuteRef.current = 0;
    runRef.current += 1;
  }, []);

  // Execute recursive function with timeout check
  const executeWithTimeout = useCallback(
    async <T>(recurFn: () => Promise<T | void>, onTimeout?: () => void, intervalMs = 3000) => {
      // Initialize start time if not set
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
        isActiveRef.current = true;
      }

      // The run this continuation belongs to — see `runRef`.
      const run = runRef.current;

      // Check if we've exceeded the maximum duration (skip if maxDuration is Infinity)
      if (maxDuration !== Infinity) {
        const elapsedTime = Date.now() - (startTimeRef.current || 0);
        if (elapsedTime >= maxDuration) {
          clearRecursiveTimeout();
          if (onTimeout) onTimeout();

          return;
        }
      }

      // Execute the recursive function
      let shouldStop: T | void;
      try {
        shouldStop = await recurFn();
      } catch (error) {
        // A `recurFn` owns its own failures and answers true/false — every caller in this repo wraps
        // its request in try/catch. Reaching here is a programming error, and the chain must not be
        // left half-alive: WITHOUT this catch the rejection propagates out of `executeWithTimeout`,
        // and since the tick below calls it with no `.catch` that is an unhandled rejection, while
        // `isActiveRef` stays true (so `isActive()` lies) and the 60 s minute-logger interval leaks
        // for the rest of the session. Retrying is the wrong default too: the same throw would
        // repeat every `intervalMs` until `maxDuration`. So tear the run down and say so loudly.
        if (runRef.current !== run) {
          return;
        }

        console.error("[useTimeoutApiCall] Recursive call threw — stopping this run", { status: httpStatusOf(error) });
        clearRecursiveTimeout();

        return;
      }

      // Obsolete continuation: the run was cleared or replaced while this call was in flight. Return
      // without touching `timeoutRef` — it belongs to whoever is running now.
      if (runRef.current !== run) {
        return shouldStop;
      }

      // If shouldStop is true or the function is no longer active, don't continue
      if (shouldStop === true || !isActiveRef.current) {
        clearTimeout(timeoutRef.current as NodeJS.Timeout);
        timeoutRef.current = null;
        startTimeRef.current = null;
        isActiveRef.current = false;

        return shouldStop;
      }

      // Setup next call - only happens if shouldStop is false
      timeoutRef.current = setTimeout(() => {
        executeWithTimeout(recurFn, onTimeout, intervalMs);
      }, intervalMs);

      return shouldStop;
    },
    [maxDuration, clearRecursiveTimeout]
  );

  // Setup minute logger
  const setupMinuteLogger = useCallback(() => {
    // Clear any existing interval
    if (minuteLoggerRef.current) {
      clearInterval(minuteLoggerRef.current);
    }

    // Start logging every minute (only in development)
    minuteLoggerRef.current = setInterval(() => {
      if (startTimeRef.current !== null && isActiveRef.current && process.env.NODE_ENV === "development") {
        const elapsedTime = Date.now() - startTimeRef.current;
        const minutes = Math.floor(elapsedTime / 60000);
        const seconds = ((elapsedTime % 60000) / 1000).toFixed(0);
        console.log(`⏱️ API call in progress: ${minutes}m ${seconds}s elapsed`);
      }
    }, 60000); // Log every 60 seconds
  }, []);

  // Start recursive API call with timeout
  const startRecursiveCall = useCallback(
    <T>(recurFn: () => Promise<T | void>, onTimeout?: () => void, intervalMs = 3000) => {
      // Clear any existing timeout first
      clearRecursiveTimeout();

      // Reset snackbar state
      dispatch(resetSnackbar());

      // Start fresh. `clearRecursiveTimeout` above already bumped the run, so any continuation still
      // in flight from the previous one is now obsolete.
      startTimeRef.current = Date.now();
      isActiveRef.current = true;

      // Setup minute logger
      setupMinuteLogger();

      return executeWithTimeout(recurFn, onTimeout, intervalMs);
    },
    [executeWithTimeout, clearRecursiveTimeout, setupMinuteLogger]
  );

  // Stop recursive calls manually
  const stopRecursiveCalls = useCallback(() => {
    clearRecursiveTimeout();
  }, [clearRecursiveTimeout]);

  // Check if calls are currently active
  const isActive = useCallback(() => {
    return isActiveRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearRecursiveTimeout();
    };
  }, [clearRecursiveTimeout]);

  return {
    startRecursiveCall,
    stopRecursiveCalls,
    isActive,
    clearRecursiveTimeout,
  };
};

export default useTimeoutApiCall;
